import React, { useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router";
import styled, { createGlobalStyle } from "styled-components";
import { C, F, ring } from "../styles";
import logoSos from "../assets/logo-sos-3d.webp";
import logoAprova from "../assets/logo-aprova-branca.svg";
import grain from "../assets/fx-grain.svg";

const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700;800&family=Noto+Sans:wght@400;500;700&family=Anton&family=Permanent+Marker&display=swap";

// Carrega as fontes do SOS só nas páginas deste módulo.
export const useFonts = () => {
  useEffect(() => {
    if (document.querySelector(`link[href="${FONTS_HREF}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = FONTS_HREF;
    document.head.appendChild(link);
  }, []);
};

export const Global = createGlobalStyle`
  body {
    margin: 0;
    background: ${C.blue};
    color: ${C.white};
    font-family: ${F.body};
    -webkit-font-smoothing: antialiased;
    line-height: normal;
  }
  #root * { box-sizing: border-box; }
  #root input::placeholder { color: #8A93C9; }
`;

const Page = styled.div`
  min-height: 100vh;
  background: ${C.blue};
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  background: ${C.black};
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const HeaderInner = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  padding: 14px clamp(16px, 4vw, 40px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  min-width: 0;
`;

const LogoBox = styled.a`
  position: relative;
  height: 46px;
  aspect-ratio: 1753 / 763;
  flex: none;
  img,
  svg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    display: block;
  }
  svg {
    pointer-events: none;
  }
`;

const Divider = styled.span`
  width: 1px;
  height: 28px;
  background: rgba(255, 255, 255, 0.2);
  flex: none;
`;

const Title = styled.span`
  font: 700 clamp(18px, 2vw, 24px) / 1.1 ${F.title};
  letter-spacing: -0.03em;
`;

const LoginBtn = styled.button`
  font: 600 13px/1 ${F.title};
  background: transparent;
  color: ${C.yellow};
  border: none;
  box-shadow: ${ring(1.5, "rgba(255,255,0,0.7)")};
  border-radius: 20px;
  padding: 9px 16px;
  cursor: pointer;
`;

const Hello = styled.span`
  font: 600 13px/1 ${F.title};
  color: ${C.pale};
`;

export const Footer = styled.footer`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 24px 16px 32px;
  margin-top: auto;
  img {
    height: 24px;
    display: block;
    filter: brightness(0) invert(1);
  }
  span {
    font-size: 12.5px;
    color: rgba(255, 255, 255, 0.55);
  }
`;

export const Grain = styled.div.attrs({ "aria-hidden": true })`
  position: fixed;
  inset: -80px;
  z-index: 70;
  pointer-events: none;
  opacity: 0.07;
  mix-blend-mode: overlay;
  background-image: url(${grain});
  background-size: 160px 160px;
`;

// Onda de "osciloscópio" desenhada sobre o O do logo, como no protótipo.
export const Scope = ({ clipId = "sos-hdr-freq", strokeWidth = 12, children }) => {
  const pathRef = useRef(null);
  useEffect(() => {
    const path = pathRef.current;
    if (!path || (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches)) return;
    const t0 = performance.now();
    let raf;
    const draw = () => {
      const t = (performance.now() - t0) / 1000;
      const g = 0.55 + 0.45 * Math.sin(t * 1.7) * Math.sin(t * 0.9 + 1);
      let d = "";
      for (let x = 722; x <= 963; x += 3) {
        const u = (x - 722) / 241;
        const env = Math.sin(Math.PI * u);
        const v = Math.sin(x * 0.21 + t * 9) * 0.55 + Math.sin(x * 0.083 - t * 5.3) * 0.3 + Math.sin(x * 0.47 + t * 14) * 0.18;
        d += (d ? " L" : "M") + x + " " + (363 + 58 * env * g * v).toFixed(1);
      }
      path.setAttribute("d", d);
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <svg aria-hidden="true" viewBox="0 0 1753 763">
      <circle cx="842" cy="363" r="122" fill="#000000" />
      <defs>
        <clipPath id={clipId}>
          <circle cx="842" cy="363" r="112" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {children}
        <path
          ref={pathRef}
          d="M720 363 L965 363"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ filter: "drop-shadow(0 0 6px rgba(108,203,239,0.9))" }}
        />
      </g>
    </svg>
  );
};

const firstName = (user) => {
  if (user && user.name) return user.name.split(" ")[0];
  return "aluno";
};

// action: "auto" mostra Entrar/Olá conforme a sessão; "none" esconde.
export default function Layout({ title, user, action = "auto", children }) {
  const history = useHistory();
  const location = useLocation();
  useFonts();

  useEffect(() => {
    document.title = `${title} | SOS Aprova`;
  }, [title]);

  const goLogin = () => history.push(`/login?next=${encodeURIComponent(location.pathname + location.search)}`);

  return (
    <Page>
      <Global />
      <Header>
        <HeaderInner>
          <Brand>
            <LogoBox href="/simulados" aria-label="SOS Aprova · Simulados">
              <img src={logoSos} alt="SOS" />
              <Scope />
            </LogoBox>
            <Divider />
            <Title>{title}</Title>
          </Brand>
          {action === "auto" &&
            (user ? <Hello>Olá, {firstName(user)}</Hello> : <LoginBtn type="button" onClick={goLogin}>Entrar com minha inscrição</LoginBtn>)}
        </HeaderInner>
      </Header>
      {children}
      <Footer>
        <img src={logoAprova} alt="aprova total" />
        <span>Conhecimento máximo. Tempo mínimo.</span>
      </Footer>
      <Grain />
    </Page>
  );
}
